import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { BrowserProvider } from "ethers";

import StakingHAI from "./StakingHAI";
import Footer from "./Footer";

const MainPage = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [address, setAddress] = useState("");
  const [showStaking, setShowStaking] = useState(false);
  const [autoDisconnectTimer, setAutoDisconnectTimer] = useState(null);

  // --------------------------------------------------
  // Check existing wallet connection
  // --------------------------------------------------
  const checkWalletConnection = useCallback(async () => {
    if (!window.ethereum) {
      setIsConnected(false);
      setAddress("");
      return;
    }

    try {
      const provider = new BrowserProvider(window.ethereum);

      // Does NOT open wallet popup
      const accounts = await provider.send("eth_accounts", []);

      if (accounts && accounts.length > 0) {
        setAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setAddress("");
        setIsConnected(false);
      }
    } catch (error) {
      console.error("Wallet connection check failed:", error);
      setAddress("");
      setIsConnected(false);
    }
  }, []);

  // --------------------------------------------------
  // Connect wallet
  // --------------------------------------------------
  const connectWallet = async () => {
    if (!window.ethereum) {
      toast.error("Please install MetaMask or a compatible wallet.");
      return;
    }

    try {
      const provider = new BrowserProvider(window.ethereum);

      // Opens wallet confirmation
      const accounts = await provider.send(
        "eth_requestAccounts",
        []
      );

      if (!accounts || accounts.length === 0) {
        toast.error("No wallet account found.");
        return;
      }

      setAddress(accounts[0]);
      setIsConnected(true);

      toast.success("Wallet connected successfully!");
    } catch (error) {
      console.error("Wallet connection error:", error);

      if (error?.code === 4001) {
        toast.error("Wallet connection rejected.");
      } else {
        toast.error(
          error?.shortMessage ||
            error?.message ||
            "Failed to connect wallet."
        );
      }
    }
  };

  // --------------------------------------------------
  // Disconnect application session
  // --------------------------------------------------
  const disconnectWallet = useCallback(() => {
    /*
      Injected wallets such as MetaMask do not expose a method
      that allows a website to forcefully disconnect the wallet.

      This clears the wallet state inside the application.
    */

    setAddress("");
    setIsConnected(false);

    toast.info("Wallet disconnected from the application.");
  }, []);

  // --------------------------------------------------
  // Listen for wallet account / network changes
  // --------------------------------------------------
  useEffect(() => {
    if (!window.ethereum) {
      return;
    }

    const handleAccountsChanged = (accounts) => {
      if (!accounts || accounts.length === 0) {
        setAddress("");
        setIsConnected(false);
        return;
      }

      setAddress(accounts[0]);
      setIsConnected(true);
    };

    const handleChainChanged = () => {
      /*
        Reload the application after network change so
        all contracts/providers use the new network.
      */
      window.location.reload();
    };

    window.ethereum.on(
      "accountsChanged",
      handleAccountsChanged
    );

    window.ethereum.on(
      "chainChanged",
      handleChainChanged
    );

    checkWalletConnection();

    return () => {
      if (window.ethereum?.removeListener) {
        window.ethereum.removeListener(
          "accountsChanged",
          handleAccountsChanged
        );

        window.ethereum.removeListener(
          "chainChanged",
          handleChainChanged
        );
      }
    };
  }, [checkWalletConnection]);

  // --------------------------------------------------
  // Auto disconnect after 10 minutes
  // --------------------------------------------------
  useEffect(() => {
    if (autoDisconnectTimer) {
      clearTimeout(autoDisconnectTimer);
      setAutoDisconnectTimer(null);
    }

    if (!isConnected) {
      return;
    }

    const timer = setTimeout(() => {
      disconnectWallet();

      toast.info(
        "Application wallet session expired after 10 minutes."
      );
    }, 600000);

    setAutoDisconnectTimer(timer);

    return () => {
      clearTimeout(timer);
    };
  }, [isConnected, disconnectWallet]);

  // --------------------------------------------------
  // Short wallet address
  // --------------------------------------------------
  const shortAddress = address
    ? `${address.substring(0, 4)}...${address.slice(-4)}`
    : "";

  return (
    <>
      {/* Left Gold Glow */}
      <div
        className="
          fixed
          left-[-100px]
          top-1/4
          w-[300px]
          h-[300px]
          rounded-full
          pointer-events-none
          z-0
          bg-gradient-to-br
          from-[#d4af37]
          to-[#b8860b]
          opacity-40
          blur-[180px]
        "
      />

      {/* Right Gold Glow */}
      <div
        className="
          fixed
          right-[-100px]
          top-1/3
          w-[300px]
          h-[300px]
          rounded-full
          pointer-events-none
          z-0
          bg-gradient-to-br
          from-[#b8860b]
          to-[#d4af37]
          opacity-40
          blur-[180px]
        "
      />

      <div className="main-content relative z-10">

        {/* ================= HEADER ================= */}
        <header className="w-full text-white px-4 py-3 flex items-center justify-between">

          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-2"
          >
            <img
              src="/kindlogo.png"
              alt="Logo"
              className="h-12 sm:h-20"
            />

            <span className="text-xl sm:text-3xl font-bold text-[#b8860b]">
              KindCat
            </span>
          </Link>

          {/* Wallet */}
          <div className="flex items-center gap-2">

            {isConnected && address && (
              <button
                onClick={disconnectWallet}
                className="
                  hidden
                  sm:block
                  px-3
                  py-2
                  rounded-md
                  text-sm
                  bg-[#1a1a1a]
                  border
                  border-[#333]
                  hover:border-[#d4af37]
                  transition
                  text-white
                "
              >
                Disconnect
              </button>
            )}

            <button
              className="
                px-4
                py-2
                bg-gradient-to-br
                from-[#d4af37]
                via-[#b8860b]
                to-[#1a1a1a]
                rounded-md
                font-semibold
                hover:brightness-110
                transition
              "
              style={{
                color: "#fff8e8",
              }}
              onClick={
                isConnected
                  ? disconnectWallet
                  : connectWallet
              }
            >
              {isConnected
                ? shortAddress
                : "Connect Wallet"}
            </button>

          </div>
        </header>

        {/* ================= STAKING ================= */}
        <StakingHAI
          onBuyClick={() => setShowStaking(false)}
        />

        {/* ================= FOOTER ================= */}
        <Footer />

      </div>
    </>
  );
};

export default MainPage;